/**
 * Form settings — the "Settings" tab of the form builder.
 * =======================================================
 * 2026-09-29 (owner: "settings page looks overcomplicated"). Replaces the
 * old Basics tab + the "WhatsApp & automation" editor (FormOptionsEditor)
 * with one page in four short sections:
 *
 *   Form        on/off, title, subtitle, colour
 *   Website     show in the website chat, which inbox, required, message
 *               box, outside-hours message
 *   WhatsApp    all at once (the published Flow) or one question at a time,
 *               plus optional intro / thank-you messages
 *   Advanced    (closed) key, type, how often, ask everyone again
 *
 * The manual WhatsApp Flow ID / screen / answer-mapping fields are gone (owner
 * decision): the only Flow is the one "Publish → WhatsApp" builds from the
 * form's own questions (backend form_service._collector_and_flow).
 *
 * Stores exactly the same values as before: the form's columns and its
 * `options` (key, kind, repeat_policy, repeat_days, collector,
 * show_in_widget, intro_text, completion_text, version).
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, MessageCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { LangMapEditor } from '../components/Automation/FormWhatsAppSettings';
import { labelOf, loadFormMeta, type FormMeta } from '../components/Automation/formMeta';

export interface FormSettingsValues {
  title: string;
  subtitle: string;
  primary_color: string;
  is_enabled: boolean;
  is_required: boolean;
  show_message_box: boolean;
  enable_business_hours_check: boolean;
  offline_message: string;
  channel_id: number | null;
  options: Record<string, any>;
}

interface Props {
  values: FormSettingsValues;
  onChange: (patch: Partial<FormSettingsValues>) => void;
  websiteInboxes: { id: number; name: string }[];
  /** The form's published WhatsApp Flow, if any. */
  publishedFlowId?: string | null;
  /** Opens Publish → WhatsApp (saved forms only). */
  onPublishWhatsApp?: () => void;
  /** Title of ANOTHER live registration form, if there is one (only one allowed). */
  otherRegistration?: string | null;
  /** Version last saved; undefined for a new form. */
  savedVersion?: number;
  savedKey?: string;
}

// A few colours that read well on the widget; any colour can still be picked.
const SWATCHES = ['#1f93ff', '#2563eb', '#0f766e', '#16a34a', '#c2410c', '#7c3aed', '#db2777', '#18181b'];

const SELECT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <section className="rounded-xl border border-border bg-card">
    <div className="border-b border-border px-5 py-3.5">
      <h3 className="text-sm font-semibold">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
    <div className="divide-y divide-border">{children}</div>
  </section>
);

const Row: React.FC<{ label: string; hint?: string; htmlFor?: string; children: React.ReactNode; stacked?: boolean }> = ({
  label,
  hint,
  htmlFor,
  children,
  stacked,
}) => (
  <div className={cn('px-5 py-3.5', stacked ? 'space-y-2' : 'flex items-center justify-between gap-6')}>
    <div className="min-w-0">
      <Label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </Label>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
    {children}
  </div>
);

const PreChatFormSettings: React.FC<Props> = ({
  values,
  onChange,
  websiteInboxes,
  publishedFlowId,
  onPublishWhatsApp,
  otherRegistration,
  savedVersion,
  savedKey,
}) => {
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  useEffect(() => {
    loadFormMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  const options = values.options || {};
  const setOptions = (patch: Record<string, any>) => {
    const next = { ...options, ...patch };
    Object.keys(next).forEach((k) => (next[k] === undefined || next[k] === null) && delete next[k]);
    onChange({ options: next });
  };

  const showInWidget = options.show_in_widget !== false;
  const kind: string = options.kind || (options.marks_registered ? 'registration' : 'profile');
  const kindDefaults = meta?.form_kinds.find((k) => k.value === kind)?.defaults || {};
  const policy = options.repeat_policy || kindDefaults.repeat_policy;
  const bumped = savedVersion !== undefined && Number(options.version || 1) > savedVersion;
  const published = !!(publishedFlowId || '').trim();
  // "All at once": the published Flow on WhatsApp, a form card on the website.
  // "One at a time": questions asked one by one everywhere (collector "chat").
  // Not chosen yet: all at once when a Flow is published, else one at a time.
  const allAtOnce = options.collector ? options.collector !== 'chat' : published;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-6">
      <Section title="Form">
        <Row label="Form is on" hint="When off, nobody is asked this form — website, link, app or WhatsApp.">
          <Switch checked={values.is_enabled} onCheckedChange={(v) => onChange({ is_enabled: v })} />
        </Row>
        <Row label="Title" htmlFor="fs-title" stacked>
          <Input id="fs-title" value={values.title} onChange={(e) => onChange({ title: e.target.value })} />
        </Row>
        <Row label="Subtitle" hint="Optional line under the title." htmlFor="fs-subtitle" stacked>
          <Input id="fs-subtitle" value={values.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} />
        </Row>
        <Row label="Colour" hint="The form's header and buttons.">
          <div className="flex items-center gap-2">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Colour ${c}`}
                onClick={() => onChange({ primary_color: c })}
                className={cn(
                  'h-7 w-7 rounded-md border border-border',
                  values.primary_color?.toLowerCase() === c && 'ring-2 ring-foreground ring-offset-2 ring-offset-card',
                )}
                style={{ backgroundColor: c }}
              />
            ))}
            <input
              type="color"
              aria-label="Pick any colour"
              value={values.primary_color || '#1f93ff'}
              onChange={(e) => onChange({ primary_color: e.target.value })}
              className="h-7 w-9 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
            />
          </div>
        </Row>
      </Section>

      <Section title="Website">
        <Row label="Show in the website chat" hint="Asked when a visitor opens the chat. Turn off for forms only sent on WhatsApp or used in the app.">
          <Switch checked={showInWidget} onCheckedChange={(v) => setOptions({ show_in_widget: v ? undefined : false })} />
        </Row>
        {showInWidget && (
          <>
            <Row label="Which website" hint="A website's own form is shown first; otherwise a form for all websites." htmlFor="fs-inbox">
              <select
                id="fs-inbox"
                className={cn(SELECT_CLS, 'w-56')}
                value={values.channel_id ?? ''}
                onChange={(e) => onChange({ channel_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">All websites</option>
                {websiteInboxes.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Must be filled in before chatting">
              <Switch checked={values.is_required} onCheckedChange={(v) => onChange({ is_required: v })} />
            </Row>
            <Row label="Let visitors add a message" hint="A message box under the questions.">
              <Switch checked={values.show_message_box} onCheckedChange={(v) => onChange({ show_message_box: v })} />
            </Row>
            <Row label="Outside business hours, show a message instead">
              <Switch
                checked={values.enable_business_hours_check}
                onCheckedChange={(v) => onChange({ enable_business_hours_check: v })}
              />
            </Row>
            {values.enable_business_hours_check && (
              <Row label="Message outside business hours" htmlFor="fs-offline" stacked>
                <Textarea
                  id="fs-offline"
                  rows={2}
                  value={values.offline_message}
                  onChange={(e) => onChange({ offline_message: e.target.value })}
                />
              </Row>
            )}
          </>
        )}
      </Section>

      <Section title="WhatsApp" hint="When an Automation rule or an agent sends this form on WhatsApp.">
        <div className="px-5 py-3.5">
          <p className="mb-2 text-sm font-medium">How the questions are asked</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              {
                on: true,
                title: 'All at once',
                text: published
                  ? 'As your published WhatsApp form. A form card on the website.'
                  : 'As a WhatsApp form once you publish it; until then one at a time. A form card on the website.',
              },
              { on: false, title: 'One question at a time', text: 'Like a chat, on WhatsApp and on the website.' },
            ].map((o) => (
              <button
                key={o.title}
                type="button"
                onClick={() => setOptions({ collector: o.on ? 'form_card' : 'chat' })}
                className={cn(
                  'rounded-lg border px-3.5 py-3 text-left transition-colors',
                  allAtOnce === o.on ? 'border-foreground/60 bg-muted/40' : 'border-border hover:bg-muted/20',
                )}
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span
                    className={cn(
                      'h-3.5 w-3.5 rounded-full border-2',
                      allAtOnce === o.on ? 'border-foreground bg-foreground' : 'border-muted-foreground',
                    )}
                  />
                  {o.title}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{o.text}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <MessageCircle className="h-3.5 w-3.5" />
            {published ? 'Published to WhatsApp.' : 'Not published to WhatsApp yet.'}
            {onPublishWhatsApp ? (
              <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onPublishWhatsApp}>
                {published ? 'Republish' : 'Publish now'}
              </Button>
            ) : (
              <span>Save the form first, then publish it from the forms list.</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMessagesOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-medium hover:bg-muted/20"
        >
          {messagesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Messages before and after (optional)
        </button>
        {messagesOpen && (
          <>
            <Row label="Before the first question" stacked>
              <LangMapEditor
                value={options.intro_text}
                onChange={(intro_text) => setOptions({ intro_text })}
                placeholder="e.g. Before I log this, a few quick questions."
              />
            </Row>
            <Row label="After the last answer" stacked>
              <LangMapEditor
                value={options.completion_text}
                onChange={(completion_text) => setOptions({ completion_text })}
                placeholder="Leave empty for the default thank-you"
              />
            </Row>
          </>
        )}
      </Section>

      <section className="rounded-xl border border-border bg-card">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-5 py-3.5 text-left"
        >
          {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="text-sm font-semibold">Advanced</span>
          <span className="text-xs text-muted-foreground">Key, type, how often people are asked</span>
        </button>
        {advancedOpen && (
          <div className="divide-y divide-border border-t border-border">
            <Row
              label="Key"
              hint={savedKey ? 'Automation rules and answers refer to it, so it can’t change.' : 'How Automation rules refer to this form.'}
              htmlFor="fs-key"
            >
              <Input
                id="fs-key"
                className="w-56 font-mono text-xs"
                value={options.key || savedKey || ''}
                disabled={!!savedKey}
                placeholder="Made from the title"
                onChange={(e) =>
                  setOptions({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 100) || undefined })
                }
              />
            </Row>
            <Row label="Type" hint={meta?.form_kinds.find((k) => k.value === kind)?.description} htmlFor="fs-kind">
              <select
                id="fs-kind"
                className={cn(SELECT_CLS, 'w-56')}
                value={kind}
                onChange={(e) =>
                  setOptions({
                    kind: e.target.value,
                    marks_registered: e.target.value === 'registration' ? true : undefined,
                    repeat_policy: undefined,
                    repeat_days: undefined,
                  })
                }
              >
                {(meta?.form_kinds || [{ value: kind, label: kind }]).map((k) => (
                  <option key={k.value} value={k.value} disabled={k.value === 'registration' && !!otherRegistration && kind !== 'registration'}>
                    {k.label}
                    {k.value === 'registration' && otherRegistration && kind !== 'registration' ? ` (already: ${otherRegistration})` : ''}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="How often the same person is asked" htmlFor="fs-repeat">
              <div className="flex items-center gap-2">
                <select
                  id="fs-repeat"
                  className={cn(SELECT_CLS, 'w-56')}
                  value={options.repeat_policy || ''}
                  onChange={(e) => setOptions({ repeat_policy: e.target.value || undefined })}
                >
                  <option value="">{labelOf(meta?.repeat_policies, kindDefaults.repeat_policy)} (default)</option>
                  {(meta?.repeat_policies || []).map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {policy === 'every_n_days' && (
                  <Input
                    type="number"
                    min={1}
                    aria-label="Days"
                    className="w-24"
                    value={options.repeat_days ?? ''}
                    placeholder={`${kindDefaults.repeat_days ?? ''} days`}
                    onChange={(e) => setOptions({ repeat_days: e.target.value === '' ? undefined : Number(e.target.value) })}
                  />
                )}
              </div>
            </Row>
            {savedVersion !== undefined && (
              <Row
                label={`Ask everyone again (version ${savedVersion + 1})`}
                hint={`After adding a required question: people who answered version ${savedVersion} are asked the new questions on WhatsApp.`}
              >
                <Switch checked={bumped} onCheckedChange={(v) => setOptions({ version: v ? savedVersion + 1 : savedVersion })} />
              </Row>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default PreChatFormSettings;
